// =============================================
// LISTA DE BANCOS - ADMIN
// Gestión de la tabla de bancos con ordenamiento y paginación
// =============================================

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    CircularProgress,
    Alert,
    Snackbar,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    TablePagination,
    TableSortLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const BankList = () => {
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Ordenamiento
    const [orderBy, setOrderBy] = useState('bank_name');
    const [order, setOrder] = useState('asc');
    
    const [formData, setFormData] = useState({
        bank_code: '',
        bank_name: '',
        is_active: true,
    });

    useEffect(() => {
        loadBanks();
    }, []);

    const loadBanks = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/banks/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBanks(response.data.banks || []);
        } catch (error) {
            console.error('Error al cargar bancos:', error);
            setSnackbar({
                open: true,
                message: 'Error al cargar bancos',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedBank(null);
        setFormData({
            bank_code: '',
            bank_name: '',
            is_active: true,
        });
        setOpenForm(true);
    };

    const handleEdit = (bank) => {
        setSelectedBank(bank);
        setFormData({
            bank_code: bank.bank_code,
            bank_name: bank.bank_name,
            is_active: bank.is_active,
        });
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedBank(null);
        setFormData({
            bank_code: '',
            bank_name: '',
            is_active: true,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            if (selectedBank) {
                await axios.put(`${API_URL}/banks/${selectedBank.id}`, formData, config);
                setSnackbar({
                    open: true,
                    message: 'Banco actualizado exitosamente',
                    severity: 'success',
                });
            } else {
                await axios.post(`${API_URL}/banks`, formData, config);
                setSnackbar({
                    open: true,
                    message: 'Banco creado exitosamente',
                    severity: 'success',
                });
            }

            loadBanks();
            handleCloseForm();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error al guardar banco',
                severity: 'error',
            });
        }
    };

    const handleDelete = async (bank) => {
        if (window.confirm(`¿Estás seguro de desactivar el banco "${bank.bank_name}"?`)) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/banks/${bank.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                loadBanks();
                setSnackbar({
                    open: true,
                    message: 'Banco desactivado exitosamente',
                    severity: 'success',
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || 'Error al desactivar banco',
                    severity: 'error',
                });
            }
        }
    };

    // Ordenamiento
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedBanks = React.useMemo(() => {
        const comparator = (a, b) => {
            if (orderBy === 'bank_code') {
                return order === 'asc' 
                    ? a.bank_code.localeCompare(b.bank_code)
                    : b.bank_code.localeCompare(a.bank_code);
            }
            if (orderBy === 'bank_name') {
                return order === 'asc'
                    ? a.bank_name.localeCompare(b.bank_name)
                    : b.bank_name.localeCompare(a.bank_name);
            }
            if (orderBy === 'is_active') {
                return order === 'asc'
                    ? (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1)
                    : (a.is_active === b.is_active ? 0 : a.is_active ? 1 : -1);
            }
            return 0;
        };

        return [...banks].sort(comparator);
    }, [banks, order, orderBy]);

    // Paginación
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedBanks = sortedBanks.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Encabezado */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4">
                        Administración de Bancos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total: {banks.length} bancos
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Nuevo Banco
                </Button>
            </Box>

            {/* Loading */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Tabla */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'bank_code'}
                                            direction={orderBy === 'bank_code' ? order : 'asc'}
                                            onClick={() => handleRequestSort('bank_code')}
                                        >
                                            Código
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'bank_name'}
                                            direction={orderBy === 'bank_name' ? order : 'asc'}
                                            onClick={() => handleRequestSort('bank_name')}
                                        >
                                            Nombre del Banco
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">
                                        <TableSortLabel
                                            active={orderBy === 'is_active'}
                                            direction={orderBy === 'is_active' ? order : 'asc'}
                                            onClick={() => handleRequestSort('is_active')}
                                        >
                                            Estado
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedBanks.map((bank) => (
                                    <TableRow key={bank.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                                {bank.bank_code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="500">
                                                {bank.bank_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={bank.is_active ? 'Activo' : 'Inactivo'}
                                                color={bank.is_active ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(bank)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {bank.is_active && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(bank)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        {/* Paginación */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={sortedBanks.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => 
                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                            }
                        />
                    </TableContainer>

                    {/* Sin datos */}
                    {banks.length === 0 && (
                        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No hay bancos registrados
                            </Typography>
                        </Paper>
                    )}
                </>
            )}

            {/* Formulario */}
            <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {selectedBank ? 'Editar Banco' : 'Nuevo Banco'}
                    </DialogTitle>

                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField
                                label="Código del Banco"
                                value={formData.bank_code}
                                onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })}
                                required
                                fullWidth
                                helperText="Ej: BG, BAC, BANISTMO"
                                inputProps={{ maxLength: 10 }}
                            />

                            <TextField
                                label="Nombre del Banco"
                                value={formData.bank_name}
                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                required
                                fullWidth
                                helperText="Ej: Banco General, BAC Credomatic"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                }
                                label="Banco Activo"
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleCloseForm}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained">
                            Guardar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default BankList;
